import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const WIZARD_KEY = 'casefile_admin_setup_done';

const STEPS = [
  {
    id: 'welcome',
    icon: '🎉',
    title: 'Welcome to CaseFile Creator!',
    subtitle: 'Your agency is set up and ready to go.',
    content: (agencyName) => (
      <div>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '16px' }}>
          You're the administrator for <strong style={{ color: '#60a5fa' }}>{agencyName || 'your agency'}</strong>.
          This quick setup guide will walk you through the essential steps to get your team up and running.
        </p>
        <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', border: '1px solid #1e3a5f' }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>What we'll cover</div>
          {['Invite your first officers', 'Understand roles & permissions', 'Set up your first case', 'Configure security settings'].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', color: '#94a3b8', fontSize: '0.9rem' }}>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>✓</span> {item}
            </div>
          ))}
        </div>
      </div>
    ),
    action: null,
  },
  {
    id: 'invite',
    icon: '👥',
    title: 'Invite Your First Officers',
    subtitle: 'Add personnel to your agency.',
    content: () => (
      <div>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '16px' }}>
          Go to the <strong style={{ color: '#f1f5f9' }}>Personnel</strong> page to invite officers, detectives, and supervisors.
          Each person will receive an email with a temporary password and must change it on first login.
        </p>
        <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '12px', fontWeight: 600 }}>Available Roles</div>
          {[
            { role: 'Admin', desc: 'Full access — manage personnel, cases, settings', color: '#f59e0b' },
            { role: 'Supervisor', desc: 'Manage cases and personnel in their unit', color: '#3b82f6' },
            { role: 'Investigator', desc: 'Create and manage cases and evidence', color: '#8b5cf6' },
            { role: 'Officer', desc: 'Create cases and add evidence', color: '#22c55e' },
            { role: 'Read-Only', desc: 'View cases and evidence only', color: '#64748b' },
          ].map(({ role, desc, color }) => (
            <div key={role} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '6px 0', borderBottom: '1px solid #1e293b' }}>
              <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: '4px', padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap', marginTop: '2px' }}>{role}</span>
              <span style={{ color: '#64748b', fontSize: '0.82rem' }}>{desc}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    action: { label: '👥 Go to Personnel', path: '/personnel' },
  },
  {
    id: 'permissions',
    icon: '🔐',
    title: 'Roles & Permissions',
    subtitle: 'Control what each officer can access.',
    content: () => (
      <div>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '16px' }}>
          Each officer's permissions can be customized individually. You can use built-in <strong style={{ color: '#f1f5f9' }}>permission templates</strong> for quick setup, or fine-tune each permission manually.
        </p>
        <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '12px', fontWeight: 600 }}>Key Permissions</div>
          {[
            { perm: 'Can Create Cases', desc: 'Open new case files' },
            { perm: 'Can Manage Evidence', desc: 'Add, edit, and transfer evidence' },
            { perm: 'Can Manage Documents', desc: 'Upload and manage case documents' },
            { perm: 'Can Manage Personnel', desc: 'Invite and manage other officers' },
            { perm: 'Can View Analytics', desc: 'Access reports and analytics dashboard' },
          ].map(({ perm, desc }) => (
            <div key={perm} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1e293b' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>{perm}</span>
              <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{desc}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    action: { label: '👥 Manage Personnel', path: '/personnel' },
  },
  {
    id: 'firstcase',
    icon: '📁',
    title: 'Create Your First Case',
    subtitle: 'Start building your case management system.',
    content: () => (
      <div>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '16px' }}>
          Cases are the core of CaseFile Creator. Each case has a full lifecycle with status tracking, evidence, documents, and a complete audit trail.
        </p>
        <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '12px', fontWeight: 600 }}>Case Lifecycle</div>
          {[
            { status: 'Open', color: '#22c55e', desc: 'Newly created, investigation not started' },
            { status: 'Active', color: '#3b82f6', desc: 'Under active investigation' },
            { status: 'Pending Review', color: '#f59e0b', desc: 'Awaiting supervisor or DA review' },
            { status: 'Closed', color: '#64748b', desc: 'Investigation complete' },
            { status: 'Archived', color: '#8b5cf6', desc: 'Long-term storage, read-only' },
          ].map(({ status, color, desc }) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0' }}>
              <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: '4px', padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700, minWidth: '90px', textAlign: 'center' }}>{status}</span>
              <span style={{ color: '#64748b', fontSize: '0.82rem' }}>{desc}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    action: { label: '📁 Create First Case', path: '/cases/new' },
  },
  {
    id: 'security',
    icon: '🛡️',
    title: 'Security & Compliance',
    subtitle: 'Configure your agency\'s security settings.',
    content: () => (
      <div>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '16px' }}>
          CaseFile Creator is built to CJIS Security Policy standards. Review these settings to ensure your agency is properly configured.
        </p>
        <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
          {[
            { icon: '🔑', title: 'Multi-Factor Authentication', desc: 'Require MFA for all officers. Highly recommended for CJIS compliance.', path: '/compliance' },
            { icon: '⏱️', title: 'Session Timeouts', desc: 'Sessions auto-expire after 30 minutes of inactivity.', path: null },
            { icon: '🔐', title: 'Password Policy', desc: 'Min 12 chars, complexity required, 90-day expiry enforced.', path: null },
            { icon: '📋', title: 'Audit Log', desc: 'Every action is logged with user, timestamp, and IP address.', path: '/timeline' },
          ].map(({ icon, title, desc }) => (
            <div key={title} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #1e293b' }}>
              <span style={{ fontSize: '1.2rem', marginTop: '2px' }}>{icon}</span>
              <div>
                <div style={{ color: '#e2e8f0', fontSize: '0.88rem', fontWeight: 600 }}>{title}</div>
                <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '2px' }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
    action: { label: '🛡️ Security Settings', path: '/compliance' },
  },
  {
    id: 'done',
    icon: '🚀',
    title: 'You\'re Ready to Go!',
    subtitle: 'Your agency is fully configured.',
    content: () => (
      <div>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '16px' }}>
          You've completed the setup guide. Here are some quick links to get started:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {[
            { icon: '👥', label: 'Invite Officers', path: '/personnel' },
            { icon: '📁', label: 'Create Case', path: '/cases/new' },
            { icon: '📊', label: 'Dashboard', path: '/' },
            { icon: '📈', label: 'Analytics', path: '/analytics' },
            { icon: '🛡️', label: 'Security', path: '/compliance' },
            { icon: '👤', label: 'My Profile', path: '/profile' },
          ].map(({ icon, label, path }) => (
            <a key={label} href={path} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#0f172a', border: '1px solid #334155', borderRadius: '8px',
              padding: '10px 14px', color: '#e2e8f0', textDecoration: 'none',
              fontSize: '0.85rem', fontWeight: 600, transition: 'border-color 0.15s'
            }}>
              <span>{icon}</span> {label}
            </a>
          ))}
        </div>
      </div>
    ),
    action: null,
  },
];

const AdminSetupWizard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  const isAdmin = user?.role === 'admin';
  const agencyName = user?.agencyName || user?.agency?.name || '';

  useEffect(() => {
    if (!isAdmin) return;
    const done = localStorage.getItem(WIZARD_KEY);
    if (!done) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, [isAdmin]);

  const handleFinish = () => {
    localStorage.setItem(WIZARD_KEY, 'true');
    setVisible(false);
  };

  const handleSkip = () => {
    localStorage.setItem(WIZARD_KEY, 'true');
    setVisible(false);
  };

  const handleAction = (path) => {
    if (path) {
      handleFinish();
      navigate(path);
    }
  };

  if (!visible || !isAdmin) return null;

  const current = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;
  const isLast = step === STEPS.length - 1;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      zIndex: 10500, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: '#1e293b', borderRadius: '18px', padding: '32px',
        width: '100%', maxWidth: '540px', border: '1px solid #334155',
        boxShadow: '0 24px 80px rgba(0,0,0,0.7)', maxHeight: '90vh', overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.8rem' }}>{current.icon}</span>
            <div>
              <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.15rem', fontWeight: 700 }}>{current.title}</h2>
              <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '0.8rem' }}>{current.subtitle}</p>
            </div>
          </div>
          <button onClick={handleSkip} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '1.3rem', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '0.72rem', color: '#475569' }}>Step {step + 1} of {STEPS.length}</span>
            <button onClick={handleSkip} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.72rem', padding: 0 }}>Skip setup</button>
          </div>
          <div style={{ height: '3px', background: '#0f172a', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #3b82f6, #6366f1)', borderRadius: '2px', transition: 'width 0.3s ease' }} />
          </div>
        </div>

        {/* Content */}
        <div style={{ marginBottom: '28px' }}>
          {current.content(agencyName)}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={{
              padding: '10px 18px', borderRadius: '8px', border: '1px solid #334155',
              background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem'
            }}>← Back</button>
          )}

          {current.action && (
            <button onClick={() => handleAction(current.action.path)} style={{
              padding: '10px 18px', borderRadius: '8px', border: '1px solid #3b82f6',
              background: 'transparent', color: '#60a5fa', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem'
            }}>{current.action.label}</button>
          )}

          <button
            onClick={isLast ? handleFinish : () => setStep(s => s + 1)}
            style={{
              marginLeft: 'auto', padding: '10px 24px', borderRadius: '8px', border: 'none',
              background: isLast ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
              color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem'
            }}
          >
            {isLast ? '🚀 Get Started' : 'Next →'}
          </button>
        </div>

        {/* Step dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px' }}>
          {STEPS.map((_, i) => (
            <div key={i} onClick={() => setStep(i)} style={{
              width: i === step ? '20px' : '7px', height: '7px',
              borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s',
              background: i === step ? '#3b82f6' : i < step ? '#1d4ed8' : '#334155'
            }} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Export restart function for Profile page
export const restartAdminWizard = () => {
  localStorage.removeItem(WIZARD_KEY);
  window.location.reload();
};

export default AdminSetupWizard;
